import React from 'react';
import { HealthScoreOverallGaugeProps } from './HealthTrackerConstants';
import limeHeart from '../../images/lime-heart.svg';
import {
  emptyOrganisationHealthScoreCopy,
  isEmptyOrganisationHealthScore,
} from '../PopulationPulse/mapResponses';
import { scoreBandScaleSegments } from '../PopulationPulse/scoreBandColor';
import { dashboardMuted } from '../../theme/dashboardLayout';

const MIN_SCORE = 0;
const MAX_SCORE = 100;

const SCALE_SEGMENTS = scoreBandScaleSegments();

const PIN_WIDTH = 72;
const PIN_HEIGHT = 104;
const PIN_STROKE = '#004b4e';
const PIN_FILL = '#ffffff';

/** Material hourglass_empty path (24×24 viewBox), scaled into the pin score box. */
const HOURGLASS_PATH =
  'M6 2v6h.01L6 8.01 10 12l-4 4 .01.01H6V22h12v-5.99h-.01L18 16l-4-4 4-3.99-.01-.01H18V2H6zm10 14.5V20H8v-3.5l4-4 4 4zm-4-5-4-4V4h8v3.5l-4 4z';

function clampScore(raw: number): number {
  if (Number.isNaN(raw)) {
    return MIN_SCORE;
  }
  return Math.min(MAX_SCORE, Math.max(MIN_SCORE, raw));
}

function scoreToPercent(score: number): number {
  return ((score - MIN_SCORE) / (MAX_SCORE - MIN_SCORE)) * 100;
}

const HealthScoreOverallGauge: React.FC<HealthScoreOverallGaugeProps> = ({
  value,
  scoreName = 'Organisation Health Score',
}) => {
  const empty = isEmptyOrganisationHealthScore(value);
  const score = empty ? MIN_SCORE : clampScore(Math.round(Number(value)));
  const positionPercent = scoreToPercent(score);
  const emptyDisclaimer = emptyOrganisationHealthScoreCopy();
  const ariaLabel = empty
    ? emptyDisclaimer
    : `${scoreName} ${score}`;

  return (
    <div className="mx-auto w-full max-w-md">
      <div
        className="mx-auto w-full max-w-[360px]"
        style={{ paddingLeft: PIN_WIDTH / 2, paddingRight: PIN_WIDTH / 2 }}
      >
        <div
          className="relative w-full pb-3"
          style={{ paddingTop: `${PIN_HEIGHT + 8}px` }}
        >
          <div
            className="pointer-events-none absolute z-[1]"
            style={{
              left: `${positionPercent}%`,
              bottom: '18px',
              transform: 'translateX(-50%)',
            }}
            role="img"
            aria-label={ariaLabel}
          >
            <ScorePin score={score} empty={empty} />
          </div>

          <div
            className="flex h-[14px] w-full overflow-hidden rounded-full shadow-[inset_0_1px_2px_rgba(0,75,77,0.15)]"
            aria-hidden
          >
            {SCALE_SEGMENTS.map((segment, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: segment.color,
                  flexGrow: segment.weight,
                  flexBasis: 0,
                }}
              />
            ))}
          </div>
        </div>
      </div>
      {empty ? (
        <p className={`mx-auto mt-1 max-w-[360px] px-2 text-center text-[0.8rem] leading-snug ${dashboardMuted}`}>
          {emptyDisclaimer}
        </p>
      ) : null}
    </div>
  );
};

function ScorePin({ score, empty }: { score: number; empty: boolean }): React.ReactElement {
  return (
    <svg
      width={PIN_WIDTH}
      height={PIN_HEIGHT}
      viewBox="0 0 72 104"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M36 102
           C 36 102, 6 78, 6 56
           C 6 41, 18 28, 36 28
           C 54 28, 66 41, 66 56
           C 66 78, 36 102, 36 102 Z"
        fill={PIN_FILL}
        stroke={PIN_STROKE}
        strokeWidth={2.5}
        strokeLinejoin="round"
      />

      <rect
        x="16"
        y="2"
        width="40"
        height="34"
        rx="6"
        fill={PIN_FILL}
        stroke={PIN_STROKE}
        strokeWidth={2.5}
      />
      {empty ? (
        <g transform="translate(24 7)">
          <path d={HOURGLASS_PATH} fill={PIN_STROKE} />
        </g>
      ) : (
        <text
          x="36"
          y="27"
          textAnchor="middle"
          fontFamily="NotoSans, sans-serif"
          fontWeight={800}
          fontSize="20"
          fill={PIN_STROKE}
        >
          {score}
        </text>
      )}

      <image
        href={limeHeart}
        x="18"
        y="44"
        width="36"
        height="32"
        preserveAspectRatio="xMidYMid meet"
      />
    </svg>
  );
}

export default HealthScoreOverallGauge;
